import type { QueryClient } from "@tanstack/react-query";
import type { SidebarItemKey } from "../../../config/sidebarConfig";
import type { AuthUser } from "../../../types/authUser";
import type { FeatureFlagKey } from "../../../utils/featureFlags";
import type { DateDirection, DateRange, DateRangeKey } from "../dateRange";
import type { DayKey } from "../taskDetails";
import type { TaskName } from "../tasks/taskConversation";

/**
 * Who is asking; loaders call the API with their token. Loaders fetch through
 * `queryClient` with the pages' own queries, so answers share the pages' data.
 */
export type AnswerContext = {
  user: AuthUser | null;
  token: string | null;
  queryClient: QueryClient;
  /** The page Ask Noticeboard was opened on */
  pathname?: string;
};

export type AnswerValues = Record<string, unknown>;

/** Where an answer's button goes; `state` is router state for the page to read on arrival. */
export type AnswerLink = { label: string; href: string; state?: unknown };

/**
 * The details a question was asked with, as remembered for follow-ups. A task's
 * details stay as the person wrote them ("Grade 2") and are looked up each time.
 */
export type SlotValues = {
  dateRange?: DateRangeKey;
  /** Who a Notice is for: a Circle, a User, or Everyone */
  audience?: string;
  topic?: string;
  /** What a Notice should say, when the request gives it word for word: "saying …", "titled X saying …" */
  message?: string;
  day?: DayKey;
  /** A Notice that isn't a plain one: "an urgent notice", "a poll". "reminder" is Send reminder on a Notice already out, never a new Notice. */
  noticeType?: "urgent" | "poll" | "reminder";
  /** The kind they chose after "What kind of notice would you like to send?" */
  noticeKind?: string;
  template?: string;
  draft?: string;
  /** The message that asked about a Notice, Circle or sender; the answer finds the name in it */
  notice?: string;
  circle?: string;
  sender?: string;
  /** An event for the Internal team only: "staff only", "staff meeting", "just for the team" */
  staffOnly?: boolean;
  /** Who it's from, as written: "from Grade R" */
  from?: string;
  /** An email address or phone number, as written: who an Invite goes to */
  contact?: string;
  /** The message that asked about a child's family; the answer finds the child in it */
  child?: string;
  /** A User: as written when adding them to a Circle ("Jane"), or the message that asks about them */
  user?: string;
  /** A Team member: as written when adding them to the Team ("Lebo Dlamini"), or the message that asks about them */
  member?: string;
  /** A Team role: as written when adding someone ("Creator"), or the message that asks about one */
  role?: string;
  /** The message that asks for worksheets; the answer finds the grade and kind in it */
  worksheet?: string;
  /** An Award: its name as written when starting one ("Maths Olympiad – Gold"), or the message that asks about one */
  award?: string;
  /** The message that asks for a Drive file; the answer finds the name in it */
  file?: string;
};

export type SlotName = keyof SlotValues;

/** Those details worked out for this moment, ready to load and word an answer. */
export type FilledSlots = {
  dateRange?: DateRange;
  notice?: string;
  circle?: string;
  sender?: string;
  child?: string;
  user?: string;
  member?: string;
  role?: string;
  worksheet?: string;
  award?: string;
  file?: string;
};

/** Where a handoff opens: a page, with router state for it to read on arrival. */
export type HandoffTarget = { pathname: string; state: unknown };

/** One line of a handoff's summary card, e.g. To: Grade 2 */
export type SummaryLine = { label: string; value: string };

/** How a task went: ready to open, a follow-up question with choices, or a reason it can't start. */
export type HandoffStart =
  | { kind: "ready"; text: string; summary: SummaryLine[]; to: HandoffTarget }
  | {
      kind: "ask";
      slot: SlotName;
      /** "notFound": what they said matched nothing, so it may not have been an answer at all */
      reason: "missing" | "ambiguous" | "notFound";
      text: string;
      choices: string[];
    }
  | { kind: "cannot"; text: string };

/** One short follow-up question with choices, for a detail that's missing, unclear or not found. */
export type SlotQuestion = Extract<HandoffStart, { kind: "ask" }>;

export const isSlotQuestion = (value: unknown): value is SlotQuestion =>
  (value as SlotQuestion | null)?.kind === "ask";

type EntryBase = {
  /** Stable id, e.g. "users.unjoined" */
  id: string;
  /** Only available to people who can open this sidebar item */
  sidebarItem: SidebarItemKey;
  /** Other sidebar items they must also be able to open, for answers drawn from those pages' data */
  alsoNeeds?: SidebarItemKey[];
  /** An opt-in feature the answer's screen needs turned on, e.g. Family on Users */
  featureFlag?: FeatureFlagKey;
  /** A rule of the page's own on top of its sidebar item's, e.g. which sections Library shows */
  visibleTo?: (user: AuthUser | null | undefined) => boolean;
  /** 10–20 phrasings to match against; also the test questions. The first is shown as an example question. */
  examples: string[];
};

/** A question Ask Noticeboard answers with a value and a link. */
export type AnswerEntry<V extends AnswerValues = AnswerValues> = EntryBase & {
  /** The details this question takes, each with the value used when the question doesn't say */
  slots?: SlotValues;
  /** Which way a named day or week looks: "what's on Monday?" is the coming Monday. Back by default. */
  dates?: DateDirection;
  answer: {
    /** The values to word, or a follow-up question when a detail it needs isn't clear */
    load: (slots: FilledSlots, ctx: AnswerContext) => Promise<V | SlotQuestion>;
    /** Used in rotation: the wording varies, the facts never do. `asked` is the message, so one entry can answer a name question and a create question differently. */
    templates: Array<(values: V, slots: FilledSlots, asked?: string) => string>;
    link: (slots: FilledSlots, values: V) => AnswerLink;
    /** The thing this answer named ("Sports Day", "Easter 2025"), so a later "it" ("Who has seen it?") can mean it. */
    subjectOf?: (values: V) => string | undefined;
    /** Every title this answer named, so the next message can ask about one of them. */
    mentionsOf?: (values: V) => string[] | undefined;
  };
  handoff?: never;
  howTo?: never;
  followUpSlot?: never;
};

/**
 * A task Ask Noticeboard starts: it fills in an existing screen for the person
 * to check. It never creates, sends or changes anything itself. Its examples
 * leave the details out ("Send a notice"), as they're matched without them.
 */
export type HandoffEntry = EntryBase & {
  /** Fills in the screen from the request, asking at most one follow-up; not needed when the task is always guided */
  handoff?: (said: SlotValues, ctx: AnswerContext) => Promise<HandoffStart>;
  /** The guided task a request starts instead, asking for each detail in turn; null to use `handoff` */
  guided?: (said: SlotValues) => TaskName | null;
  /** Which detail a bare follow-up ("same for X") changes, once this is `ready` and nothing specific was just asked. Defaults to "audience". */
  followUpSlot?: SlotName;
  slots?: never;
  dates?: never;
  answer?: never;
  howTo?: never;
};

/** Where a how-to answer's button goes: a page already known, or a phrase that begins a guided task. */
export type HowToAction =
  | { kind: "open"; label: string; to: HandoffTarget }
  | { kind: "start"; label: string; ask: string };

/**
 * A "how do I…?" question matched like any other answer (so it's never read as a
 * request to start the task), whose reply ends with one or two buttons that do
 * the real thing: open a page with nothing left to fill in, or start a guided
 * task by asking the exact question that does.
 */
export type HowToEntry = EntryBase & {
  howTo: {
    /** `asked` is the message matched to this entry, for wording that depends on what was asked (e.g. only mentioning the Library for a worksheet, not any file). */
    load: (ctx: AnswerContext, asked: string) => Promise<{ text: string; actions: HowToAction[] }>;
  };
  slots?: never;
  dates?: never;
  answer?: never;
  handoff?: never;
  guided?: never;
  followUpSlot?: never;
};

/** One question or task Ask Noticeboard knows. */
export type CatalogueEntry<V extends AnswerValues = AnswerValues> = AnswerEntry<V> | HandoffEntry | HowToEntry;

/** Lets an entry type its own values while living in the catalogue's single list. */
export const defineEntry = <V extends AnswerValues>(entry: AnswerEntry<V>) =>
  entry as unknown as CatalogueEntry;
